import type { DiscoveryStep } from "@scoach/types";
import { Spinner } from "@scoach/ui";
import { Brain, Check } from "@scoach/ui/icons";

/**
 * Animated "what I'm doing now" loader shown while a discovery runs (~2 min).
 * Mirrors the summary loading overlay's gradient + pulse and lists the research
 * steps, checking each off as it completes.
 */
export function DiscoveryProgress({
  companyName,
  steps,
}: {
  companyName: string;
  steps: DiscoveryStep[];
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 22,
        padding: "48px 24px",
        maxWidth: 560,
        margin: "0 auto",
      }}
    >
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: 20,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--primary)",
          boxShadow: "0 8px 24px rgba(99, 102, 241, 0.25)",
          animation: "discoveryPulse 2s ease-in-out infinite",
        }}
      >
        <Brain size={30} style={{ color: "#fff" }} />
      </div>
      <style>{`@keyframes discoveryPulse { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.06); opacity: .85; } }`}</style>

      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text-1)" }}>
          Researching {companyName}
        </div>
        <div style={{ fontSize: 13, color: "var(--text-3)", marginTop: 6 }}>
          Gathering everything worth knowing before your meeting — this takes a couple of minutes.
        </div>
      </div>

      <div
        style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%", maxWidth: 380 }}
      >
        {steps.map((s) => (
          <div
            key={s.key}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontSize: 13.5,
              color:
                s.state === "done"
                  ? "var(--text-2)"
                  : s.state === "active"
                    ? "var(--text-1)"
                    : "var(--text-4)",
              fontWeight: s.state === "active" ? 600 : 400,
            }}
          >
            <span
              style={{
                width: 22,
                height: 22,
                borderRadius: "50%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flex: "0 0 auto",
                background:
                  s.state === "done"
                    ? "var(--gc-green, #1e8e3e)"
                    : s.state === "active"
                      ? "var(--gc-blue-50, #e8f0fe)"
                      : "var(--surface-2)",
                border: s.state === "pending" ? "1px solid var(--border-soft)" : "none",
              }}
            >
              {s.state === "done" ? (
                <Check size={13} style={{ color: "#fff" }} />
              ) : s.state === "active" ? (
                <Spinner size={13} />
              ) : null}
            </span>
            {s.label}
          </div>
        ))}
      </div>
    </div>
  );
}
